import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wand2, Video, PenTool, Activity, MessagesSquare, Film,
  Mic, ScreenShare, Hand, PhoneOff, ImagePlus, UserCheck,
  Play, FileText, Sparkles, Eye, EyeOff, Star, Send, CheckCircle2,
} from "lucide-react";

/* ============================================================
   FEATURE SHOWCASE — Hero-level animated demo of every flagship.
   Eight tabs, auto-rotating. Light theme. PIP teacher in call panel.
   ============================================================ */

const ROTATE_MS = 7000;
const TEACHER_PHOTO = "https://customer-assets.emergentagent.com/job_ai-tutor-hub-299/artifacts/nwo7y9k6_ChatGPT%20Image%20Jan%2025%2C%202026%2C%2012_01_43%20PM.png";

// Stock student portraits (Unsplash – stable hosted IDs).
const STUDENTS = [
  { name: "Eesa",  photo: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&q=70&auto=format&fit=crop&facepad=2", role: "Grade 9", speaking: true,  raised: false, mic: true },
  { name: "Mohib", photo: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&q=70&auto=format&fit=crop&facepad=2", role: "Grade 9", speaking: false, raised: true,  mic: true },
  { name: "John",  photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=70&auto=format&fit=crop&facepad=2", role: "Grade 9", speaking: false, raised: false, mic: false },
  { name: "Vidya", photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=70&auto=format&fit=crop&facepad=2", role: "Grade 9", speaking: false, raised: false, mic: true },
];

const TABS = [
  { id: "curriculum", label: "Curriculum → animated lesson", icon: Wand2,         kicker: "AI lesson builder" },
  { id: "call",       label: "Live audio + video class",      icon: Video,         kicker: "Live class" },
  { id: "board",      label: "Smart whiteboard",               icon: PenTool,       kicker: "Teach visually" },
  { id: "focus",      label: "Concentration analysis",         icon: Activity,      kicker: "Engagement AI" },
  { id: "tutor",      label: "24/7 AI tutor",                  icon: MessagesSquare,kicker: "Always-on help" },
  { id: "recording",  label: "Recorded lectures",              icon: Film,          kicker: "Auto-transcripts" },
  { id: "image",      label: "AI animated story & visuals",    icon: ImagePlus,     kicker: "Storybook on demand" },
  { id: "profile",    label: "Public profile · lead capture",  icon: UserCheck,     kicker: "Students from Google" },
];

export default function FeatureShowcase({ embedded = false }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setActive((i) => (i + 1) % TABS.length), ROTATE_MS);
    return () => clearTimeout(t);
  }, [active, paused]);

  const current = TABS[active];
  const Wrapper = embedded ? "div" : "section";

  return (
    <Wrapper className={embedded ? "" : "py-24 lg:py-32 bg-[#FBFAF7]"}>
      <div className={embedded ? "" : "max-w-[1320px] mx-auto px-6 lg:px-10"}>
        {!embedded && (
          <div className="grid grid-cols-12 gap-8 items-start mb-12">
            <div className="col-span-12 lg:col-span-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#FFB400] font-mono">// SEE IT IN ACTION</div>
              <h2 className="mt-4 font-display font-semibold tracking-[-0.035em] text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.02]">
                Every feature, <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>animated for clarity.</span>
              </h2>
            </div>
            <p className="col-span-12 lg:col-span-5 lg:col-start-8 text-[16.5px] leading-[1.55] text-neutral-700 lg:mt-12">
              Eight flagship tools that make your teaching delightful. Click any tab — or watch them rotate.
            </p>
          </div>
        )}

        {/* Tab strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-6"
             onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          {TABS.map((t, i) => (
            <TabButton key={t.id} t={t} active={i === active} onClick={() => setActive(i)} progress={i === active && !paused} />
          ))}
        </div>

        <div className="relative rounded-2xl border border-black/10 bg-white overflow-hidden shadow-[0_30px_80px_-30px_rgba(10,12,16,0.15)] min-h-[560px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
              className="absolute inset-0"
            >
              {current.id === "curriculum" && <CurriculumPanel />}
              {current.id === "call" && <CallPanel />}
              {current.id === "board" && <WhiteboardPanel />}
              {current.id === "focus" && <ConcentrationPanel />}
              {current.id === "tutor" && <TutorPanel />}
              {current.id === "recording" && <RecordingPanel />}
              {current.id === "image" && <ImageGenPanel />}
              {current.id === "profile" && <PublicProfilePanel />}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-4 text-center text-[12px] text-neutral-500 font-mono uppercase tracking-[0.2em]">
          {current.kicker} · {String(active + 1).padStart(2, "0")} / {String(TABS.length).padStart(2, "0")}
        </div>
      </div>
    </Wrapper>
  );
}

function TabButton({ t, active, onClick, progress }) {
  return (
    <button
      onClick={onClick}
      data-testid={`feature-tab-${t.id}`}
      className={`relative text-left px-3 py-2.5 rounded-xl border transition-all overflow-hidden ${active ? "bg-[#0A0C10] border-transparent text-white" : "bg-white border-black/8 hover:border-black/20"}`}
    >
      <div className="flex items-center gap-1.5">
        <t.icon size={13} className={active ? "text-[#FFD600]" : "text-[#FFB400]"} strokeWidth={1.8} />
        <div className={`text-[9.5px] uppercase tracking-[0.16em] font-mono ${active ? "text-neutral-400" : "text-neutral-500"}`}>{t.kicker}</div>
      </div>
      <div className={`mt-0.5 text-[12px] font-semibold leading-tight ${active ? "text-white" : ""}`}>{t.label}</div>
      {progress && (
        <motion.div
          key={t.id + "-bar"}
          className="absolute left-0 bottom-0 h-[2px] bg-[#FFD600]"
          initial={{ width: "0%" }} animate={{ width: "100%" }}
          transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
        />
      )}
    </button>
  );
}

/* ============================================================
   PANEL 01 — Curriculum → animated lesson
   ============================================================ */
function CurriculumPanel() {
  const lessons = [
    { title: "What is photosynthesis?", sub: "Definition · 4 mins" },
    { title: "The chloroplast", sub: "Visual breakdown · 6 mins" },
    { title: "Light & dark reactions", sub: "Animated diagram · 8 mins" },
    { title: "Why it matters for the planet", sub: "Real-world · 5 mins" },
  ];
  return (
    <div className="grid grid-cols-12 h-full">
      <div className="col-span-12 md:col-span-5 p-7 border-r border-black/8 bg-[#FAF9F6] flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">// input</div>
        <div className="mt-2 font-display font-semibold text-[20px] tracking-tight">Paste your syllabus.</div>
        <div className="mt-4 rounded-xl border border-black/10 bg-white p-3.5 text-[12.5px] text-neutral-700 leading-relaxed font-mono flex-1 min-h-[180px]">
          <span className="text-[#FFB400]">Chapter 3 — Photosynthesis</span><br />
          • Definition and importance<br />
          • Structure of chloroplast<br />
          • Light-dependent reactions<br />
          • Calvin cycle<br />
          • Environmental factors<br />
          <span className="text-neutral-400">// 1,234 words pasted</span>
        </div>
        <motion.button
          initial={{ scale: 1 }} animate={{ scale: [1, 1.03, 1] }} transition={{ duration: 1.4, repeat: Infinity }}
          className="mt-4 w-full bg-[#0A0C10] text-white text-[13px] font-semibold rounded-full py-2.5 inline-flex items-center justify-center gap-2"
        >
          <Sparkles size={14} className="text-[#FFD600]" /> Generate animated lesson
        </motion.button>
      </div>

      <div className="col-span-12 md:col-span-7 p-7 relative overflow-hidden">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">// AI output · animated lesson</div>
        <div className="mt-2 flex items-center justify-between">
          <div className="font-display font-semibold text-[20px] tracking-tight">Chapter 3 · 4 lessons ready</div>
          <div className="text-[11px] text-neutral-500 font-mono">12 seconds</div>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {lessons.map((l, i) => (
            <motion.div
              key={l.title}
              initial={{ opacity: 0, y: 18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.4 + i * 0.4, duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
              className="rounded-xl border border-black/8 bg-white p-3 group hover:shadow-[0_8px_20px_-8px_rgba(10,12,16,0.1)] transition-shadow"
            >
              <div className="aspect-video rounded-md bg-gradient-to-br from-[#FFF4E5] via-white to-[#E8EEFF] flex items-center justify-center mb-2 relative overflow-hidden">
                <motion.div
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ delay: 0.6 + i * 0.4, type: "spring", stiffness: 200, damping: 18 }}
                  className="w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center"
                >
                  <Play size={14} className="text-[#FFB400] fill-[#FFB400] ml-0.5" />
                </motion.div>
                <div className="absolute top-1.5 left-1.5 text-[9px] font-mono uppercase tracking-[0.15em] bg-white/80 backdrop-blur px-1.5 py-0.5 rounded">Lesson {i + 1}</div>
              </div>
              <div className="text-[12.5px] font-semibold leading-tight">{l.title}</div>
              <div className="text-[10.5px] text-neutral-500 mt-0.5">{l.sub}</div>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.4, duration: 0.6 }}
          className="mt-5 text-[11px] text-neutral-500 font-mono"
        >
          ✓ Quizzes · ✓ Homework · ✓ Visuals · ✓ Voice-over scripts — all auto-generated
        </motion.div>
      </div>
    </div>
  );
}

/* ============================================================
   PANEL 02 — Live Class (light theme · teacher PIP · 4 students)
   ============================================================ */
function CallPanel() {
  return (
    <div className="grid grid-cols-12 h-full bg-[#FAF9F6]">
      <div className="col-span-12 md:col-span-9 p-6 lg:p-8 flex flex-col relative">
        {/* Top status bar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white bg-[#FF6058] px-2 py-1 rounded-full flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-500">14:00 — 15:00 · Grade 9 Algebra · 4 in class</span>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <Pill light>Interactive Board</Pill><Pill light>Screen capture</Pill><Pill light>Quiz</Pill>
          </div>
        </div>

        {/* Student grid */}
        <div className="grid grid-cols-2 gap-3 flex-1">
          {STUDENTS.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              className={`relative rounded-xl overflow-hidden aspect-video bg-neutral-200 border-2 ${s.speaking ? "border-[#28C941]" : "border-transparent"} shadow-sm`}
            >
              <img src={s.photo} alt={s.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />

              {/* Top-right badges */}
              <div className="absolute top-2 right-2 flex gap-1">
                {s.raised && (
                  <motion.div
                    animate={{ rotate: [0, 12, -8, 12, 0] }} transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 1 }}
                    className="bg-[#FFD600] rounded-full w-6 h-6 flex items-center justify-center shadow"
                  >
                    <Hand size={12} className="text-[#0A0C10]" />
                  </motion.div>
                )}
                {s.speaking && (
                  <div className="bg-[#28C941] text-white rounded-full px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.15em] flex items-center gap-1">
                    <SpeakingBars /> Speaking
                  </div>
                )}
              </div>

              {/* Bottom-left name + mic */}
              <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                <div className={`rounded-full w-5 h-5 flex items-center justify-center ${s.mic ? "bg-black/40 backdrop-blur" : "bg-[#FF6058]"}`}>
                  <Mic size={10} className="text-white" />
                </div>
                <div className="text-white text-[12px] font-semibold drop-shadow">{s.name}</div>
              </div>
              <div className="absolute bottom-2 right-2 text-[9px] font-mono text-white/80 bg-black/30 backdrop-blur px-1.5 py-0.5 rounded">{s.role}</div>
            </motion.div>
          ))}
        </div>

        {/* Teacher PIP — bottom-right of stage */}
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.6, duration: 0.5 }}
          className="absolute bottom-24 right-7 w-40 sm:w-48 aspect-[4/3] rounded-xl overflow-hidden shadow-[0_18px_40px_-12px_rgba(10,12,16,0.35)] border-2 border-white"
        >
          <img src={TEACHER_PHOTO} alt="Teacher" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          <div className="absolute top-1.5 left-1.5 bg-[#FFD600] text-[#0A0C10] text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 rounded font-semibold">You · teaching</div>
          <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5">
            <div className="bg-[#28C941] rounded-full px-1.5 py-0.5 flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.15em] text-white">
              <SpeakingBars /> Live
            </div>
          </div>
          {/* Animated speaking ring */}
          <motion.div
            className="absolute inset-0 rounded-xl pointer-events-none"
            style={{ borderColor: "#28C941", borderWidth: 2 }}
            animate={{ opacity: [0.6, 0.2, 0.6] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          />
        </motion.div>

        {/* Controls */}
        <div className="mt-5 mx-auto bg-white border border-black/10 rounded-full px-2 py-1.5 flex items-center gap-1.5 shadow-sm">
          <CtrlBtn icon={Mic} active />
          <CtrlBtn icon={Video} active />
          <CtrlBtn icon={ScreenShare} />
          <CtrlBtn icon={Hand} />
          <CtrlBtn icon={MessagesSquare} />
          <div className="w-px h-6 bg-black/10 mx-1" />
          <button className="bg-[#FF6058] hover:bg-[#FF4040] rounded-full p-2 transition-colors">
            <PhoneOff size={14} className="text-white" />
          </button>
        </div>
      </div>

      {/* Right rail — chat */}
      <div className="hidden md:flex col-span-3 border-l border-black/8 bg-white flex-col p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">Class chat</div>
        <div className="space-y-2 text-[12px] flex-1">
          <ChatLine name="Mohib" text="Sir 🙋 can you explain step 2 again?" />
          <ChatLine name="Vidya" text="Got it, thanks 🙏" />
          <ChatLine name="You" text="Sure Mohib — re-doing it on the board." me />
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}>
            <ChatLine name="John" text="Could you share the worksheet later?" />
          </motion.div>
        </div>
        <div className="mt-3 rounded-full bg-[#F3F1ED] px-3 py-2 text-[11px] text-neutral-500">Type a message…</div>
      </div>
    </div>
  );
}

function CtrlBtn({ icon: Icon, active }) {
  return (
    <button className={`rounded-full p-2 transition-colors ${active ? "bg-[#0A0C10] text-white" : "bg-transparent text-neutral-600 hover:bg-black/5"}`}>
      <Icon size={14} />
    </button>
  );
}

function Pill({ children, light }) {
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.15em] ${light ? "bg-white border border-black/10 text-neutral-600" : "bg-white/10 backdrop-blur text-white"}`}>{children}</span>
  );
}

function SpeakingBars() {
  return (
    <span className="flex items-end gap-[1.5px] h-2.5">
      {[0, 1, 2].map((i) => (
        <motion.span key={i} className="w-[2px] bg-white rounded-full"
          animate={{ height: ["30%", "100%", "60%", "100%", "30%"] }}
          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }} />
      ))}
    </span>
  );
}

function ChatLine({ name, text, me }) {
  return (
    <div>
      <div className="text-[9.5px] text-neutral-500 font-mono mb-0.5">{name}</div>
      <div className={`rounded-lg px-2.5 py-1.5 ${me ? "bg-[#FFD600] text-[#0A0C10] font-medium" : "bg-[#F3F1ED] text-neutral-800"}`}>{text}</div>
    </div>
  );
}

/* ============================================================
   PANEL 03 — Smart whiteboard
   ============================================================ */
function WhiteboardPanel() {
  return (
    <div className="h-full grid grid-cols-12 bg-[#F8F7F2]">
      <div className="col-span-1 hidden md:flex flex-col items-center gap-3 py-6 border-r border-black/8 bg-white">
        {[
          { c: "#0A0C10" }, { c: "#FFB400" }, { c: "#002FA7" }, { c: "#28C941" }, { c: "#FFD600" },
        ].map((t, i) => <div key={i} className="w-6 h-6 rounded-full border-2 border-white shadow" style={{ background: t.c }} />)}
        <div className="w-6 h-px bg-black/10" />
        <PenTool size={16} className="text-neutral-500" />
        <FileText size={16} className="text-neutral-500" />
      </div>

      <div className="col-span-12 md:col-span-11 relative p-6 lg:p-10">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-2">// whiteboard · grade 9 algebra</div>
        <div className="font-display font-semibold text-[20px] tracking-tight mb-6">Solving quadratic by factoring</div>
        <svg viewBox="0 0 800 380" className="w-full max-h-[400px]">
          <motion.text x="40" y="50" fontSize="28" fontFamily="Outfit, sans-serif" fontWeight="600" fill="#0A0C10"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>x² − 5x + 6 = 0</motion.text>
          <motion.line x1="40" y1="62" x2="270" y2="62" stroke="#FFD600" strokeWidth="6"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 1.0, duration: 0.6 }} />
          <motion.text x="40" y="120" fontSize="22" fontFamily="Outfit, sans-serif" fill="#0A0C10"
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.6, duration: 0.5 }}>(x − 2)(x − 3) = 0</motion.text>
          <motion.path d="M 310 110 Q 360 90, 420 105" stroke="#FFB400" strokeWidth="3" fill="none" strokeLinecap="round"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 2.2, duration: 0.6 }} />
          <motion.path d="M 415 100 L 420 105 L 415 112" stroke="#FFB400" strokeWidth="3" fill="none" strokeLinecap="round"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.8, duration: 0.2 }} />
          <motion.text x="430" y="100" fontSize="14" fontFamily="Instrument Serif, serif" fontStyle="italic" fill="#FFB400"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.6, duration: 0.5 }}>factor</motion.text>
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.2, duration: 0.6 }}>
            <text x="40" y="200" fontSize="22" fontFamily="Outfit, sans-serif" fill="#0A0C10">x − 2 = 0</text>
            <text x="220" y="200" fontSize="22" fontFamily="Outfit, sans-serif" fill="#0A0C10">or</text>
            <text x="290" y="200" fontSize="22" fontFamily="Outfit, sans-serif" fill="#0A0C10">x − 3 = 0</text>
          </motion.g>
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 4.0, duration: 0.5 }}>
            <text x="40" y="260" fontSize="26" fontFamily="Outfit, sans-serif" fontWeight="600" fill="#28C941">x = 2</text>
            <text x="200" y="260" fontSize="22" fontFamily="Outfit, sans-serif" fill="#0A0C10">or</text>
            <text x="280" y="260" fontSize="26" fontFamily="Outfit, sans-serif" fontWeight="600" fill="#28C941">x = 3</text>
            <motion.ellipse cx="80" cy="252" rx="62" ry="22" stroke="#28C941" strokeWidth="2.5" fill="none"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 4.4, duration: 0.7 }} />
            <motion.ellipse cx="320" cy="252" rx="62" ry="22" stroke="#28C941" strokeWidth="2.5" fill="none"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 4.8, duration: 0.7 }} />
          </motion.g>
          <motion.g initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3.5, duration: 0.5 }}>
            <rect x="500" y="150" width="240" height="120" rx="6" fill="#FFF4A0" stroke="#FFD600" strokeWidth="1" transform="rotate(-2 620 210)" />
            <text x="520" y="180" fontSize="11" fontFamily="JetBrains Mono, monospace" fill="#0A0C10" transform="rotate(-2 620 210)">// Pro tip</text>
            <text x="520" y="205" fontSize="14" fontFamily="Outfit, sans-serif" fill="#0A0C10" transform="rotate(-2 620 210)">Look for two numbers</text>
            <text x="520" y="225" fontSize="14" fontFamily="Outfit, sans-serif" fill="#0A0C10" transform="rotate(-2 620 210)">that multiply to +6</text>
            <text x="520" y="245" fontSize="14" fontFamily="Outfit, sans-serif" fill="#0A0C10" transform="rotate(-2 620 210)">and add to −5.</text>
          </motion.g>
          <motion.g
            animate={{ x: [40, 270, 430, 290, 700, 320, 700], y: [40, 50, 100, 200, 200, 260, 260] }}
            transition={{ duration: 5.5, times: [0, 0.16, 0.4, 0.55, 0.7, 0.85, 1], ease: "easeInOut" }}
          >
            <circle r="6" fill="#FFB400" />
            <circle r="11" fill="#FFB400" opacity="0.25" />
          </motion.g>
        </svg>
      </div>
    </div>
  );
}

/* ============================================================
   PANEL 04 — Concentration / engagement
   ============================================================ */
function ConcentrationPanel() {
  const students = [
    { name: "Eesa",  focus: 96, status: "focused",    color: "#28C941" },
    { name: "Mohib", focus: 88, status: "focused",    color: "#28C941" },
    { name: "Vidya", focus: 72, status: "engaged",    color: "#FFD600" },
    { name: "John",  focus: 41, status: "distracted", color: "#FF6058" },
    { name: "Sara",  focus: 91, status: "focused",    color: "#28C941" },
    { name: "Liam",  focus: 64, status: "engaged",    color: "#FFD600" },
  ];
  return (
    <div className="h-full grid grid-cols-12">
      <div className="col-span-12 md:col-span-8 p-7">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">// AI engagement monitor · live</div>
        <div className="mt-2 flex items-center justify-between">
          <div className="font-display font-semibold text-[20px] tracking-tight">Class focus map · 14:32</div>
          <div className="text-[11px] font-mono text-neutral-500">refreshing every 5s</div>
        </div>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {students.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06, duration: 0.4 }}
              className={`relative rounded-xl border p-3 ${s.status === "distracted" ? "border-[#FF6058]/40 bg-[#FFF3F3]" : "border-black/8 bg-white"}`}
            >
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full" style={{ background: `linear-gradient(135deg, ${s.color}, #FFD600)` }} />
                  <motion.div className="absolute inset-0 rounded-full"
                    style={{ borderColor: s.color, borderWidth: 2 }}
                    animate={{ scale: [1, 1.25, 1], opacity: [0.7, 0, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }} />
                </div>
                <div className="min-w-0">
                  <div className="text-[12px] font-semibold truncate">{s.name}</div>
                  <div className="text-[10px] text-neutral-500 capitalize flex items-center gap-1">
                    {s.status === "distracted" ? <EyeOff size={9} className="text-[#FF6058]" /> : <Eye size={9} className="text-[#28C941]" />} {s.status}
                  </div>
                </div>
              </div>
              <div className="mt-2.5">
                <div className="flex justify-between text-[10px] font-mono mb-1">
                  <span className="text-neutral-500">focus</span><span style={{ color: s.color }} className="font-semibold">{s.focus}%</span>
                </div>
                <div className="h-1.5 bg-[#F3F1ED] rounded-full overflow-hidden">
                  <motion.div className="h-full rounded-full" style={{ background: s.color }}
                    initial={{ width: 0 }} animate={{ width: `${s.focus}%` }} transition={{ duration: 1, delay: 0.3 + i * 0.05 }} />
                </div>
              </div>
              {s.status === "distracted" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
                  className="mt-2 text-[10px] text-[#FF6058] font-medium">⚠ Drop noticed 2 min ago</motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      <div className="col-span-12 md:col-span-4 border-l border-black/8 bg-[#FAF9F6] p-7 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">Overall class</div>
        <div className="relative w-44 h-44 mx-auto">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#F3F1ED" strokeWidth="9" />
            <motion.circle cx="50" cy="50" r="42" fill="none" stroke="url(#focusGrad)" strokeWidth="9" strokeLinecap="round"
              pathLength={1}
              initial={{ strokeDasharray: 1, strokeDashoffset: 1 }}
              animate={{ strokeDashoffset: 1 - 0.78 }}
              transition={{ duration: 1.6, ease: [0.2, 0.7, 0.2, 1] }} />
            <defs>
              <linearGradient id="focusGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#28C941" /><stop offset="100%" stopColor="#FFD600" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-display font-semibold text-4xl tracking-tight">78%</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-mono">avg focus</div>
          </div>
        </div>
        <div className="mt-6 space-y-2.5 text-[12px]">
          <Row k="Most engaged moment" v="14:18 — candy-bar example" />
          <Row k="Weak topics tagged" v="Quadratics · Factoring" />
          <Row k="AI suggestion" v="Run a 3-Q quiz now" />
        </div>
        <motion.button
          initial={{ scale: 1 }} animate={{ scale: [1, 1.03, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
          className="mt-auto bg-[#0A0C10] text-white text-[12.5px] font-semibold rounded-full py-2.5 inline-flex items-center justify-center gap-2"
        >
          <Sparkles size={13} className="text-[#FFD600]" /> Re-engage class now
        </motion.button>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[0.15em] text-neutral-500 font-mono">{k}</div>
      <div className="font-medium text-[12.5px] mt-0.5">{v}</div>
    </div>
  );
}

/* ============================================================
   PANEL 05 — AI Tutor chat
   ============================================================ */
function TutorPanel() {
  const messages = [
    { role: "student", text: "Hey, can you help me with x² − 5x + 6 = 0?", delay: 0.2 },
    { role: "ai", text: "Of course! This is a classic quadratic. Let's solve it by factoring.", delay: 1.1 },
    { role: "ai", text: "We need two numbers that multiply to +6 and add to −5. Can you spot them?", delay: 2.1 },
    { role: "student", text: "−2 and −3?", delay: 3.5 },
    { role: "ai", text: "Perfect! 🎯 So (x−2)(x−3) = 0, which gives x = 2 or x = 3. Want me to throw 3 practice questions your way?", delay: 4.3, hasButtons: true },
  ];
  return (
    <div className="h-full grid grid-cols-12">
      <div className="col-span-12 md:col-span-3 bg-[#FAF9F6] border-r border-black/8 p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">AI Tutor · sessions</div>
        <div className="space-y-1">
          {["Quadratics — today", "Photosynthesis — yest.", "Newton's laws — Mon"].map((s, i) => (
            <div key={s} className={`text-[12px] px-2.5 py-2 rounded-lg ${i === 0 ? "bg-white border border-black/10 font-medium" : "text-neutral-600"}`}>{s}</div>
          ))}
        </div>
        <div className="mt-6 rounded-xl bg-[#FFF4E5] border border-[#FFD600]/50 p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] font-mono text-[#A35200]">Credits left</div>
          <div className="text-lg font-display font-semibold mt-1">128 / 200</div>
          <div className="h-1.5 bg-white/70 rounded-full overflow-hidden mt-2">
            <motion.div initial={{ width: 0 }} animate={{ width: "64%" }} transition={{ duration: 1.2 }} className="h-full bg-[#FFB400]" />
          </div>
        </div>
      </div>

      <div className="col-span-12 md:col-span-9 p-6 lg:p-8 flex flex-col">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFB400] to-[#FFD600] flex items-center justify-center">
            <Sparkles size={14} className="text-white" />
          </div>
          <div>
            <div className="font-display font-semibold text-[16px] tracking-tight">AI Tutor</div>
            <div className="text-[11px] text-neutral-500 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#28C941]" /> Online · usually replies in 2s</div>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto pr-2">
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: m.delay, duration: 0.4 }}
              className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-3.5 py-2.5 text-[13px] leading-relaxed ${
                m.role === "student" ? "bg-[#0A0C10] text-white rounded-2xl rounded-br-md"
                : "bg-[#F3F1ED] text-[#0A0C10] rounded-2xl rounded-bl-md"}`}>
                {m.text}
                {m.hasButtons && (
                  <div className="flex gap-2 mt-2">
                    <button className="text-[11px] bg-[#FFD600] text-[#0A0C10] font-semibold rounded-full px-3 py-1">Yes, quiz me</button>
                    <button className="text-[11px] bg-white border border-black/10 rounded-full px-3 py-1">Explain step 2</button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 5.5, duration: 0.4 }} className="flex justify-start">
            <div className="bg-[#F3F1ED] rounded-2xl rounded-bl-md px-3.5 py-2.5 flex items-center gap-1.5">
              {[0, 1, 2].map((d) => (
                <motion.span key={d} className="w-1.5 h-1.5 rounded-full bg-neutral-400"
                  animate={{ y: [0, -3, 0] }} transition={{ duration: 0.8, repeat: Infinity, delay: d * 0.15 }} />
              ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-4 rounded-full bg-white border border-black/10 px-4 py-2.5 flex items-center gap-3 text-[12.5px]">
          <input className="flex-1 outline-none bg-transparent" placeholder="Ask anything — homework, exam prep, doubts…" />
          <button className="bg-[#0A0C10] text-white rounded-full p-1.5"><Sparkles size={12} className="text-[#FFD600]" /></button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PANEL 06 — Recorded lectures
   ============================================================ */
function RecordingPanel() {
  const chapters = [
    { t: "00:00", title: "Intro & today's goal", active: false },
    { t: "02:14", title: "Quadratic form recap", active: false },
    { t: "08:32", title: "Factoring technique", active: true },
    { t: "16:05", title: "Worked example — Grade 9", active: false },
    { t: "22:48", title: "Common mistakes", active: false },
    { t: "28:12", title: "Homework + AI quiz link", active: false },
  ];
  return (
    <div className="h-full grid grid-cols-12">
      <div className="col-span-12 md:col-span-8 p-6 lg:p-8">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">// recording · 32:18 min</div>
        <div className="mt-2 font-display font-semibold text-[20px] tracking-tight">Grade 9 Algebra · 14 Oct</div>
        <div className="mt-5 rounded-xl overflow-hidden bg-[#0A0C10] aspect-video relative">
          <img src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&auto=format&fit=crop" alt="" className="w-full h-full object-cover opacity-65" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10] via-transparent to-transparent" />
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/40 backdrop-blur text-white text-[10px] px-2 py-1 rounded font-mono uppercase tracking-[0.15em]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF6058]" /> REC · 32:18
          </div>
          <motion.div initial={{ scale: 0.95 }} animate={{ scale: [0.96, 1.04, 0.96] }} transition={{ duration: 2.4, repeat: Infinity }} className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)]">
              <Play size={22} className="text-[#0A0C10] fill-[#0A0C10] ml-1" />
            </div>
          </motion.div>
          <div className="absolute bottom-3 left-3 right-3">
            <div className="relative h-1.5 bg-white/20 rounded-full overflow-hidden">
              <motion.div className="h-full bg-[#FFB400] rounded-full"
                initial={{ width: 0 }} animate={{ width: "32%" }} transition={{ duration: 2, ease: "easeOut" }} />
              {[7, 25, 50, 70, 88].map((p) => (
                <div key={p} className="absolute top-0 bottom-0 w-px bg-white/40" style={{ left: `${p}%` }} />
              ))}
            </div>
            <div className="flex justify-between mt-1.5 text-[10px] text-white/70 font-mono"><span>08:32</span><span>32:18</span></div>
          </div>
        </div>
        <div className="mt-5 rounded-xl border border-black/8 bg-[#FAF9F6] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-2 flex items-center gap-1.5">
            <FileText size={11} /> AI-generated transcript · scrolls with video
          </div>
          <div className="space-y-1.5 text-[12.5px] leading-relaxed">
            {[
              { t: "08:28", text: "Alright, now that we've recapped the standard form,", active: false },
              { t: "08:34", text: "let's see how factoring helps us crack these quadratics.", active: true },
              { t: "08:41", text: "We need two numbers that multiply to +6 and add to −5.", active: false },
              { t: "08:48", text: "Anyone want to guess?", active: false },
            ].map((line, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.15, duration: 0.4 }}
                className={`flex gap-3 ${line.active ? "bg-[#FFF4E5] rounded -mx-2 px-2 py-1" : ""}`}>
                <span className="text-[10.5px] font-mono text-neutral-500 shrink-0 w-12">{line.t}</span>
                <span className={line.active ? "font-medium text-[#0A0C10]" : "text-neutral-700"}>{line.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="col-span-12 md:col-span-4 border-l border-black/8 bg-white p-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">AI Chapters</div>
        <div className="space-y-1">
          {chapters.map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.07, duration: 0.4 }}
              className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer ${c.active ? "bg-[#FFF4E5] border border-[#FFD600]/40" : "hover:bg-[#F3F1ED]"}`}>
              <div className={`text-[10.5px] font-mono mt-0.5 ${c.active ? "text-[#A35200]" : "text-neutral-500"}`}>{c.t}</div>
              <div className="min-w-0">
                <div className={`text-[12.5px] leading-tight ${c.active ? "font-semibold" : ""}`}>{c.title}</div>
                {c.active && <div className="text-[10px] text-[#A35200] mt-0.5">▶ playing</div>}
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-5 rounded-xl bg-[#0A0C10] text-white p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] font-mono text-neutral-400">AI summary</div>
          <div className="text-[12px] mt-1.5 leading-relaxed">
            Ahmad covered factoring with a candy-bar metaphor. <span className="text-[#FFD600]">Engagement peaked at 92%</span> during the worked example. 2 students need a follow-up on discriminants.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PANEL 07 — AI Animated Story / Illustration generator
   ============================================================ */
function ImageGenPanel() {
  const stories = [
    { prompt: "A Snapshot Map of the Ghaznavid Empire — Grade 8 history",        chip: "History",  title: "A Snapshot Map of the Ghaznavid Empire" },
    { prompt: "How a seed becomes a tree — Grade 4 science storybook",            chip: "Science",  title: "How a Seed Becomes a Tree" },
    { prompt: "Pythagoras and the secret of triangles — Grade 7 math adventure", chip: "Math",     title: "Pythagoras and the Secret of Triangles" },
  ];
  const previews = [
    "https://customer-assets.emergentagent.com/job_ai-tutor-hub-299/artifacts/i33z0jab_image.png",
    "https://customer-assets.emergentagent.com/job_ai-tutor-hub-299/artifacts/i33z0jab_image.png",
    "https://customer-assets.emergentagent.com/job_ai-tutor-hub-299/artifacts/i33z0jab_image.png",
  ];
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % stories.length), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="h-full grid grid-cols-12">
      {/* Prompt + generation */}
      <div className="col-span-12 md:col-span-5 p-7 border-r border-black/8 bg-[#FAF9F6] flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">// prompt · pick a chapter</div>
        <div className="mt-2 font-display font-semibold text-[20px] tracking-tight">Turn any topic into a story.</div>
        <div className="mt-4 rounded-xl border border-black/10 bg-white p-3.5 min-h-[150px] flex-1">
          <AnimatePresence mode="wait">
            <motion.div key={active}
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.3 }}
              className="text-[14px] leading-relaxed text-[#0A0C10] font-medium">
              {stories[active].prompt}
              <span className="inline-block w-[2px] h-[14px] bg-[#0A0C10] ml-[1px] align-middle animate-pulse" />
            </motion.div>
          </AnimatePresence>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {["animated story", "diagram", "comic strip", "slide"].map((c, i) => (
              <span key={c} className={`text-[10px] px-2 py-1 rounded-full ${i === 0 ? "bg-[#0A0C10] text-white" : "bg-[#F3F1ED] text-neutral-600"}`}>{c}</span>
            ))}
          </div>
        </div>
        <motion.button initial={{ scale: 1 }} animate={{ scale: [1, 1.03, 1] }} transition={{ duration: 1.4, repeat: Infinity }}
          className="mt-4 w-full bg-[#0A0C10] text-white text-[13px] font-semibold rounded-full py-2.5 inline-flex items-center justify-center gap-2">
          <Sparkles size={14} className="text-[#FFD600]" /> Generate animated story · 10 credits
        </motion.button>
      </div>

      {/* Output — storybook viewer */}
      <div className="col-span-12 md:col-span-7 p-7 relative bg-gradient-to-br from-[#FFF7E8] via-[#FBFAF7] to-[#FFF1E0]">
        {/* Top bar like a book viewer */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFB400] font-mono">ANIMATED STORY</div>
            <AnimatePresence mode="wait">
              <motion.div key={"t-" + active}
                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.4 }}
                className="font-display font-bold text-[18px] tracking-tight text-[#0A0C10] mt-0.5 max-w-md leading-tight"
              >
                {stories[active].title}
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="bg-white border border-black/10 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium shadow-sm">
            <span className="text-neutral-500">←</span> Back
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-white/70 rounded-full overflow-hidden mb-4">
          <motion.div key={"p-" + active} className="h-full bg-gradient-to-r from-[#FFD600] via-[#FFB400] to-[#FF6058] rounded-full"
            initial={{ width: 0 }} animate={{ width: "62%" }} transition={{ duration: 1.2, ease: "easeOut" }} />
        </div>

        {/* Main storybook image */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div key={"big-" + active}
              initial={{ opacity: 0, scale: 0.96, rotate: -0.6 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.97, rotate: 0.4 }}
              transition={{ duration: 0.55, ease: [0.2, 0.7, 0.2, 1] }}
              className="rounded-2xl overflow-hidden border border-[#E9C28F] shadow-[0_18px_40px_-12px_rgba(180,120,40,0.25)] bg-[#FFFDF8]"
            >
              <img src={previews[active]} alt={stories[active].title}
                   className="w-full aspect-[5/3] object-cover" />
            </motion.div>
          </AnimatePresence>

          {/* Sparkle overlay */}
          <motion.div
            className="absolute top-3 right-3 text-[#FFD600]"
            animate={{ scale: [1, 1.25, 1], rotate: [0, 14, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles size={20} />
          </motion.div>

          {/* Chip + actions */}
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <span className="bg-white/95 backdrop-blur text-[#0A0C10] text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 rounded-full border border-black/10 shadow-sm">
              {stories[active].chip} · Grade-ready
            </span>
            <span className="bg-[#0A0C10] text-white text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 rounded-full">
              12s
            </span>
          </div>
        </div>

        {/* Thumbnails */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          {previews.map((p, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`relative aspect-[5/3] rounded-lg overflow-hidden border-2 transition-all ${i === active ? "border-[#FFB400]" : "border-transparent hover:border-black/15"}`}
            >
              <img src={p} alt="" className="w-full h-full object-cover" />
              <div className="absolute top-1 right-1 text-[8.5px] font-mono bg-white/90 px-1 py-0.5 rounded">v{i + 1}</div>
            </button>
          ))}
        </div>

        {/* Action chips */}
        <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-neutral-600">
          <span className="bg-white/80 border border-black/8 px-2 py-1 rounded-full">✓ Send to class</span>
          <span className="bg-white/80 border border-black/8 px-2 py-1 rounded-full">✓ Embed in lesson</span>
          <span className="bg-white/80 border border-black/8 px-2 py-1 rounded-full">✓ Animate (Ken-Burns)</span>
          <span className="bg-white/80 border border-black/8 px-2 py-1 rounded-full">✓ Regenerate</span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PANEL 08 — Public profile · lead capture
   ============================================================ */
function PublicProfilePanel() {
  const sources = [
    { src: "Google · 'math tutor grade 9'", at: "Just now", color: "#FFB400" },
    { src: "Direct link",                  at: "2 min ago", color: "#002FA7" },
    { src: "Instagram bio",                at: "8 min ago", color: "#A82A6E" },
    { src: "Google · 'physics online'",    at: "14 min ago", color: "#FFB400" },
  ];
  return (
    <div className="h-full grid grid-cols-12">
      {/* Profile preview */}
      <div className="col-span-12 md:col-span-7 p-7 bg-[#FAF9F6] border-r border-black/8 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">// public profile · teachingsoftware.ai/p/ahmad-raza</div>
        <div className="mt-2 font-display font-semibold text-[20px] tracking-tight">Your SEO-optimized profile</div>

        <div className="mt-4 rounded-2xl bg-white border border-black/10 p-5 flex-1 relative overflow-hidden">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-[#FFD600]">
              <img src={TEACHER_PHOTO} alt="Tutor" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-display font-semibold text-[18px] tracking-tight">Ahmad Raza</div>
              <div className="text-[11.5px] text-neutral-500">Math + Physics · Grades 6 – 12 · 12 yrs · India</div>
              <div className="mt-1 flex items-center gap-1 text-[11px]">
                {[1,2,3,4,5].map((i) => <Star key={i} size={10} className="text-[#FFD600] fill-[#FFD600]" />)}
                <span className="text-neutral-500 ml-1">4.9 · 124 students taught</span>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2 text-[11px]">
            <div className="bg-[#F3F1ED] rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500 font-mono">subjects</div>
              <div className="font-medium mt-0.5">Math, Physics, Stats</div>
            </div>
            <div className="bg-[#F3F1ED] rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500 font-mono">available</div>
              <div className="font-medium mt-0.5">Mon-Fri 4-9pm</div>
            </div>
            <div className="bg-[#F3F1ED] rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500 font-mono">demo</div>
              <div className="font-medium mt-0.5">Free 30 min</div>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-dashed border-black/15 p-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-neutral-500 mb-2">// demo class booking</div>
            <div className="space-y-1.5">
              <motion.div initial={{ width: 0 }} animate={{ width: "60%" }} transition={{ delay: 0.4, duration: 0.6 }}
                className="h-7 bg-[#F3F1ED] rounded-md" />
              <motion.div initial={{ width: 0 }} animate={{ width: "85%" }} transition={{ delay: 0.6, duration: 0.6 }}
                className="h-7 bg-[#F3F1ED] rounded-md" />
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.4 }}
                className="bg-[#FFD600] text-[#0A0C10] text-[12px] font-semibold rounded-full px-3 py-2 inline-flex items-center gap-1.5 mt-1">
                <Send size={11} /> Request demo class
              </motion.div>
            </div>
          </div>

          <div aria-hidden className="absolute -top-10 -right-10 w-40 h-40 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,122,89,0.15), transparent 70%)" }} />
        </div>
      </div>

      {/* Live leads + SEO */}
      <div className="col-span-12 md:col-span-5 p-7 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">// live leads</div>
        <div className="mt-2 font-display font-semibold text-[20px] tracking-tight">New students, hands-free.</div>

        <div className="mt-5 space-y-2 flex-1">
          {sources.map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.18, duration: 0.5 }}
              className="rounded-xl border border-black/8 bg-white p-3 flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${s.color}22`, color: s.color }}>
                <UserCheck size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] font-semibold">Lead from {s.src}</div>
                <div className="text-[10.5px] text-neutral-500">{s.at} · auto-emailed to you</div>
              </div>
              <CheckCircle2 size={14} className="text-[#28C941]" />
            </motion.div>
          ))}
        </div>

        <div className="mt-5 rounded-xl bg-[#0A0C10] text-white p-4">
          <div className="text-[10px] uppercase tracking-[0.15em] font-mono text-neutral-400">SEO health</div>
          <div className="mt-2 flex items-center justify-between text-[12px]">
            <span>Schema, meta tags, GEO</span>
            <span className="text-[#28C941] font-mono">98 / 100</span>
          </div>
          <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: "98%" }} transition={{ duration: 1.4 }} className="h-full bg-[#28C941]" />
          </div>
          <div className="mt-2 text-[11px] text-neutral-400">Ranking for 14 long-tail tutor keywords</div>
        </div>
      </div>
    </div>
  );
}
